import { ContractGroup } from 'modules/contractsPage/components/helper';
import {
  ContractsSelectors,
  ContractsThunks,
} from 'modules/contractsPage/redux';
import { DocumentationPathsEnum } from 'modules/documentationPage/routes';
import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import {
  NewRequestSelectors,
  NewRequestThunks,
} from 'modules/newRequestPage/redux';
import { ActionsPathsEnum } from 'modules/newRequestPage/routes';
import {
  Button,
  ButtonType,
  Label,
  PageWithHeader,
  SelectWithLabel,
} from 'modules/ui';
import { Modal } from 'modules/ui/modal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { FormButtons, FormContainer } from 'modules/ui/form';
import { ValidationType } from '../../../../types/ValidationType';
import { useForm } from '../../../ui/form/useForm';
import {
  StyledMessage,
  StyledSelectWithMessage,
  StyledWarningIcon,
} from './styled';

export interface ContractTerminationData {
  activeContract?: string;
  document?: string;
}

export const EarlyTermination: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const documents = useAppSelector(NewRequestSelectors.selectDocuments);
  const activeContracts = useAppSelector(ContractsSelectors.selectContracts);

  const [formData, setFormData] = useState<ContractTerminationData>({});
  const [showModal, setShowModal] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);

  const [validate, setValidate] = useState<boolean>(false);
  const { validateForm } = useForm();

  const handleSendingRequest = (event: any) => {
    event.preventDefault();
    setValidate(true);

    if (!validateForm(event)) {
      setShowModal(true);
    }
  };

  useEffect(() => {
    dispatch(NewRequestThunks.fetchEarlyTerminationDocuments());
    dispatch(
      ContractsThunks.fetchContracts({
        page: 1,
        size: 1000,
        contractGroup: ContractGroup.ACTIVE,
      }),
    );
  }, [dispatch]);

  const handleCancelSubmit = useCallback(() => {
    setShowModal(false);
    formData.activeContract &&
      formData.document &&
      dispatch(
        NewRequestThunks.sendEarlyTerminationRequest({
          contractNumber: formData.activeContract,
          document: formData.document,
        }),
      );
    history.push(ActionsPathsEnum.earlyTerminationRequestSuccess);
  }, [dispatch, formData.activeContract, formData.document, history]);

  useEffect(() => {
    if (!showModal && submitClicked) {
      handleCancelSubmit();
    }
  }, [showModal, submitClicked, handleCancelSubmit]);

  const documentsForSelect = useMemo(
    () =>
      documents?.map((document) => ({
        label: document.name,
        value: document.name,
      })) || [],
    [documents],
  );

  const requestsForSelect = useMemo(
    () =>
      activeContracts?.map((contract: any) => ({
        value: contract.number,
        label: contract.number,
      })) || [],
    [activeContracts],
  );

  return (
    <PageWithHeader
      title={t('newRequestPage.earlyTermination')}
      breadcrumbs={[
        {
          text: t('newRequestPage.breadcrumbs.newRequest'),
          url: ActionsPathsEnum.newRequestPage,
        },
        { text: t('newRequestPage.breadcrumbs.earlyTermination'), url: '' },
      ]}>
      <FormContainer noValidate onSubmit={handleSendingRequest}>
        <SelectWithLabel
          required
          label={t('newRequestPage.earlyTerminationPage.activeContract')}
          placeholderText={t('newRequestPage.earlyTerminationPage.select')}
          defaultValue={formData?.activeContract}
          options={requestsForSelect}
          onChange={(value) =>
            setFormData({ ...formData, activeContract: value })
          }
          validate={validate}
          validators={[ValidationType.REQUIRED]}
        />
        <StyledSelectWithMessage width="100%">
          <SelectWithLabel
            required
            label={t('newRequestPage.earlyTerminationPage.document')}
            placeholderText={t('newRequestPage.earlyTerminationPage.select')}
            defaultValue={formData?.document}
            options={documentsForSelect}
            onChange={(value) => setFormData({ ...formData, document: value })}
            disabled={!documentsForSelect?.length}
            validate={validate}
            validators={[ValidationType.REQUIRED]}
          />
          {!documentsForSelect?.length && (
            <StyledMessage>
              <StyledWarningIcon />
              <Label>
                {t('newRequestPage.earlyTerminationPage.documentWarning')}
              </Label>
              <Button
                type={ButtonType.Text}
                text={t('newRequestPage.earlyTerminationPage.addDocument')}
                onClick={() =>
                  history.push(DocumentationPathsEnum.documentationPage)
                }
              />
            </StyledMessage>
          )}
        </StyledSelectWithMessage>

        <FormButtons text={t('newRequestPage.earlyTerminationPage.submit')} />
      </FormContainer>

      <Modal
        title={t('newRequestPage.earlyTermination')}
        text={`${t('newRequestPage.earlyTerminationPage.modalText')} ${
          formData?.activeContract
        }?`}
        visible={showModal}
        primaryButtonText={t('newRequestPage.earlyTerminationPage.send')}
        secondaryButtonText={t('newRequestPage.earlyTerminationPage.cancel')}
        primaryAction={() => {
          setShowModal(false);
          setSubmitClicked(true);
        }}
        secondaryAction={() => setShowModal(false)}
        onClose={() => setShowModal(false)}
      />
    </PageWithHeader>
  );
};
